import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const PaymentGatewaySettings = () => {
  const { user, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [stripeConfig, setStripeConfig] = useState({
    publishableKey: "",
    secretKey: "",
    enabled: false,
  });
  const [paypalApiConfig, setPaypalApiConfig] = useState({
    clientId: "",
    clientSecret: "",
    enabled: false,
  });
  const [paypalMeConfig, setPaypalMeConfig] = useState({
    username: "",
    enabled: false,
  });
  const [coinpaymentsConfig, setCoinpaymentsConfig] = useState({
    merchantId: "",
    publicKey: "",
    privateKey: "",
    ipnSecret: "",
    enabled: false,
  });
  const [coinbaseConfig, setCoinbaseConfig] = useState({
    apiKey: "",
    webhookSecret: "",
    enabled: false,
  });
  const [nowpaymentsConfig, setNowpaymentsConfig] = useState({
    apiKey: "",
    ipnSecret: "",
    enabled: false,
  });
  const [binancepayConfig, setBinancepayConfig] = useState({ // Thêm binancepay
    apiKey: "",
    secretKey: "",
    enabled: false,
  });
  const [usernameError, setUsernameError] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({ method: "get", url: "/api/admin/payment-gateways" });
      const { stripe, paypal_api, paypal_me, coinpayments, coinbase, nowpayments, binancepay } = res.data;

      if (stripe) setStripeConfig({ ...stripe });
      if (paypal_api) setPaypalApiConfig({ ...paypal_api });
      if (paypal_me) setPaypalMeConfig({ ...paypal_me });
      if (coinpayments) setCoinpaymentsConfig({ ...coinpayments });
      if (coinbase) setCoinbaseConfig({ ...coinbase });
      if (nowpayments) setNowpaymentsConfig({ ...nowpayments });
      if (binancepay) setBinancepayConfig({ ...binancepay }); // Thêm binancepay

      setLoading(false);
    } catch (error) {
      setError("Không thể tải thông tin cấu hình cổng thanh toán!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  const validateUsername = (username) => {
    const usernameRegex = /^[a-zA-Z0-9]+$/;
    if (username && !usernameRegex.test(username)) {
      return "Username PayPal.me không hợp lệ! Chỉ chấp nhận chữ cái và số, không chứa ký tự đặc biệt hoặc @.";
    }
    return null;
  };

  const handleSaveSettings = async () => {
    try {
      const usernameValidationError = validateUsername(paypalMeConfig.username);
      if (usernameValidationError) {
        setUsernameError(usernameValidationError);
        return;
      }

      setError(null);
      setSuccessMessage(null);
      setUsernameError(null);

      console.log("Dữ liệu gửi lên API:", { stripeConfig, paypalApiConfig, paypalMeConfig, coinpaymentsConfig, coinbaseConfig, nowpaymentsConfig, binancepayConfig });

      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/payment-gateways",
        data: {
          stripe: stripeConfig,
          paypal_api: paypalApiConfig,
          paypal_me: paypalMeConfig,
          coinpayments: coinpaymentsConfig,
          coinbase: coinbaseConfig,
          nowpayments: nowpaymentsConfig,
          binancepay: binancepayConfig, // Thêm binancepay
        },
      });
      setSuccessMessage(response.data.message || "Lưu cấu hình thành công!");
    } catch (error) {
      setError(error.response?.data?.message || "Không thể lưu cấu hình cổng thanh toán!");
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadSettings();
  }, [user, navigate, loadSettings, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Cấu hình cổng thanh toán</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && <p className="text-green-500 mb-4">{successMessage}</p>}

      {/* Stripe Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình Stripe</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Publishable Key</label>
            <input
              type="text"
              value={stripeConfig.publishableKey}
              onChange={(e) => setStripeConfig({ ...stripeConfig, publishableKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Secret Key</label>
            <input
              type="text"
              value={stripeConfig.secretKey}
              onChange={(e) => setStripeConfig({ ...stripeConfig, secretKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={stripeConfig.enabled}
              onChange={(e) => setStripeConfig({ ...stripeConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt Stripe</label>
          </div>
        </div>
      </div>

      {/* PayPal API Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình PayPal API (PayPalButtons)</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Client ID</label>
            <input
              type="text"
              value={paypalApiConfig.clientId}
              onChange={(e) => setPaypalApiConfig({ ...paypalApiConfig, clientId: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Client Secret</label>
            <input
              type="text"
              value={paypalApiConfig.clientSecret}
              onChange={(e) => setPaypalApiConfig({ ...paypalApiConfig, clientSecret: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={paypalApiConfig.enabled}
              onChange={(e) => setPaypalApiConfig({ ...paypalApiConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt PayPal API</label>
          </div>
        </div>
      </div>

      {/* PayPal.me Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình PayPal.me (Username)</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">
              Username PayPal.me (ví dụ: duytoannvt)
            </label>
            <input
              type="text"
              value={paypalMeConfig.username}
              onChange={(e) => {
                const newUsername = e.target.value;
                setPaypalMeConfig({ ...paypalMeConfig, username: newUsername });
                setUsernameError(validateUsername(newUsername));
              }}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                usernameError ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Nhập username PayPal.me (chỉ chứa chữ cái và số, không chứa @)"
            />
            {usernameError && <p className="text-red-500 text-sm mt-1">{usernameError}</p>}
            <p className="text-gray-500 text-sm mt-1">
              Lưu ý: Username là phần sau "paypal.me/" (ví dụ: paypal.me/duytoannvt thì nhập "duytoannvt").
            </p>
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={paypalMeConfig.enabled}
              onChange={(e) => setPaypalMeConfig({ ...paypalMeConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt PayPal.me</label>
          </div>
        </div>
      </div>

      {/* CoinPayments Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình CoinPayments (Tiền điện tử)</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Merchant ID</label>
            <input
              type="text"
              value={coinpaymentsConfig.merchantId}
              onChange={(e) => setCoinpaymentsConfig({ ...coinpaymentsConfig, merchantId: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Merchant ID từ CoinPayments"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Public Key</label>
            <input
              type="text"
              value={coinpaymentsConfig.publicKey}
              onChange={(e) => setCoinpaymentsConfig({ ...coinpaymentsConfig, publicKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Public Key từ CoinPayments"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Private Key</label>
            <input
              type="text"
              value={coinpaymentsConfig.privateKey}
              onChange={(e) => setCoinpaymentsConfig({ ...coinpaymentsConfig, privateKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Private Key từ CoinPayments"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">IPN Secret</label>
            <input
              type="text"
              value={coinpaymentsConfig.ipnSecret}
              onChange={(e) => setCoinpaymentsConfig({ ...coinpaymentsConfig, ipnSecret: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập IPN Secret từ CoinPayments"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={coinpaymentsConfig.enabled}
              onChange={(e) => setCoinpaymentsConfig({ ...coinpaymentsConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt CoinPayments</label>
          </div>
        </div>
      </div>

      {/* Coinbase Commerce Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình Coinbase Commerce (Tiền điện tử)</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">API Key</label>
            <input
              type="text"
              value={coinbaseConfig.apiKey}
              onChange={(e) => setCoinbaseConfig({ ...coinbaseConfig, apiKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập API Key từ Coinbase Commerce"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Webhook Shared Secret</label>
            <input
              type="text"
              value={coinbaseConfig.webhookSecret}
              onChange={(e) => setCoinbaseConfig({ ...coinbaseConfig, webhookSecret: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Webhook Shared Secret từ Coinbase Commerce"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={coinbaseConfig.enabled}
              onChange={(e) => setCoinbaseConfig({ ...coinbaseConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt Coinbase Commerce</label>
          </div>
        </div>
      </div>

      {/* NOWPayments Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình NOWPayments (Tiền điện tử)</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">API Key</label>
            <input
              type="text"
              value={nowpaymentsConfig.apiKey}
              onChange={(e) => setNowpaymentsConfig({ ...nowpaymentsConfig, apiKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập API Key từ NOWPayments"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">IPN Secret</label>
            <input
              type="text"
              value={nowpaymentsConfig.ipnSecret}
              onChange={(e) => setNowpaymentsConfig({ ...nowpaymentsConfig, ipnSecret: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập IPN Secret từ NOWPayments"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={nowpaymentsConfig.enabled}
              onChange={(e) => setNowpaymentsConfig({ ...nowpaymentsConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt NOWPayments</label>
          </div>
        </div>
      </div>

      {/* Binance Pay Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình Binance Pay (Tiền điện tử)</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">API Key</label>
            <input
              type="text"
              value={binancepayConfig.apiKey}
              onChange={(e) => setBinancepayConfig({ ...binancepayConfig, apiKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập API Key từ Binance Pay"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Secret Key</label>
            <input
              type="text"
              value={binancepayConfig.secretKey}
              onChange={(e) => setBinancepayConfig({ ...binancepayConfig, secretKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Secret Key từ Binance Pay"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={binancepayConfig.enabled}
              onChange={(e) => setBinancepayConfig({ ...binancepayConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt Binance Pay</label>
          </div>
        </div>
      </div>

      <button
        onClick={handleSaveSettings}
        disabled={usernameError}
        className={`w-full px-6 py-3 rounded-lg transition duration-200 font-semibold text-lg ${
          usernameError
            ? "bg-gray-400 text-white cursor-not-allowed"
            : "bg-blue-500 text-white hover:bg-blue-600"
        }`}
      >
        Lưu cấu hình
      </button>
    </div>
  );
};

export default PaymentGatewaySettings;